import { Link } from "react-router-dom";
import { useAuth, useRole } from "@/context/AuthContext";
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  CATEGORY_SUBTITLES,
  featuresByCategory,
  type FeatureCard,
  type FeatureCategory,
} from "@/constants/features";
import { ArrowUpRight, Lock } from "lucide-react";
import clsx from "clsx";

/**
 * Phase 1E.1 UI polish — refreshed dashboard.
 *
 * Aesthetic notes:
 *   - Big display heading, light subtitle copy
 *   - Soft-bordered tiles with subtle hover
 *   - Pill-shaped "Live" / "Coming Soon" chips
 *   - Section subtitles for context (replaces dense headers)
 *
 * Logic is unchanged from Commit 1's dashboard — still pure
 * presentation over the FEATURES registry.
 */
export default function Dashboard() {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const buckets = featuresByCategory(isAdmin);
  const firstName = user?.name?.split(" ")[0];

  return (
    <div className="space-y-12">
      {/* ─── Hero greeting ──────────────────────────────────── */}
      <header>
        <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight text-gray-900 mb-4">
          {firstName ? (
            <>
              Welcome back,{" "}
              <span className="text-gray-400">{firstName}</span>
            </>
          ) : (
            "WeDesi Lab"
          )}
        </h1>
        <p className="text-base md:text-lg text-gray-500 max-w-2xl leading-relaxed">
          Every feature we&rsquo;re building, in one place. Pick a card to
          jump in — cards marked <em>Soon</em> are queued for the next
          sprint.
        </p>

        {user && (
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            {typeof user.points === "number" && (
              <Chip label={`${user.points} points`} />
            )}
            {user.badges && user.badges.length > 0 && (
              <Chip
                label={`${user.badges.length} ${user.badges.length === 1 ? "badge" : "badges"}`}
              />
            )}
            {isAdmin && (
              <Chip
                label="Admin"
                icon={<Lock className="w-3 h-3" />}
                tone="amber"
              />
            )}
          </div>
        )}
      </header>

      {/* ─── Sections per category ─────────────────────────── */}
      {CATEGORY_ORDER.map((category) => {
        const items = buckets[category];
        if (items.length === 0) return null;
        return (
          <FeatureSection
            key={category}
            category={category}
            items={items}
          />
        );
      })}
    </div>
  );
}

function FeatureSection({
  category,
  items,
}: {
  category: FeatureCategory;
  items: FeatureCard[];
}) {
  const subtitle = CATEGORY_SUBTITLES[category];
  return (
    <section>
      <div className="mb-5">
        <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
          {CATEGORY_LABELS[category]}
        </h2>
        {subtitle && (
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((feature) => (
          <FeatureTile key={feature.id} feature={feature} />
        ))}
      </div>
    </section>
  );
}

function FeatureTile({ feature }: { feature: FeatureCard }) {
  const Icon = feature.icon;
  const isLive = feature.status === "live";
  const isAdmin = feature.category === "admin";

  const inner = (
    <div className="flex items-start gap-3">
      <div
        className={clsx(
          "w-9 h-9 rounded-lg border flex items-center justify-center shrink-0",
          isLive
            ? isAdmin
              ? "border-amber-200 bg-amber-50"
              : "border-gray-200 bg-white"
            : "border-gray-100 bg-gray-50",
        )}
      >
        <Icon
          className={clsx(
            "w-4 h-4",
            isLive
              ? isAdmin
                ? "text-amber-700"
                : "text-gray-900"
              : "text-gray-300",
          )}
          strokeWidth={1.75}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3
            className={clsx(
              "font-medium tracking-tight truncate",
              isLive ? "text-gray-900" : "text-gray-400",
            )}
          >
            {feature.title}
          </h3>
          {!isLive && (
            <span className="text-[10px] uppercase tracking-wide text-gray-400 shrink-0">
              Soon
            </span>
          )}
          {isLive && (
            <ArrowUpRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-700 transition-colors shrink-0" />
          )}
        </div>
        <p
          className={clsx(
            "text-sm leading-relaxed mt-1",
            isLive ? "text-gray-600" : "text-gray-400",
          )}
        >
          {feature.description}
        </p>
        {feature.phase && (
          <p className="text-[11px] text-gray-400 mt-2 font-mono">
            Phase {feature.phase}
          </p>
        )}
      </div>
    </div>
  );

  const baseClass =
    "group block p-4 rounded-2xl border transition-all";

  if (!isLive) {
    return (
      <div
        aria-disabled="true"
        className={clsx(baseClass, "border-gray-100 bg-white/40 cursor-not-allowed")}
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={feature.path}
      className={clsx(
        baseClass,
        isAdmin
          ? "border-amber-100 bg-white hover:border-amber-300"
          : "border-gray-100 bg-white hover:border-gray-300",
      )}
    >
      {inner}
    </Link>
  );
}

function Chip({
  label,
  icon,
  tone = "gray",
}: {
  label: string;
  icon?: React.ReactNode;
  tone?: "gray" | "amber" | "green";
}) {
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full",
        tone === "amber"
          ? "bg-amber-50 text-amber-800"
          : tone === "green"
            ? "bg-green-100 text-green-700"
            : "bg-gray-100 text-gray-700",
      )}
    >
      {icon}
      {label}
    </span>
  );
}
